import stringSimilarity from "string-similarity";

export const MINIMUM_SEARCH_SCORE = 0.2;

/**
 * Gets a comparison score between a search string and a value.
 * The score is based on string similarity and boosts if the search string is a substring of the value.
 * The score is between 0 and 1, where 1 means very good match and 0 means no match.
 * If the search string is shorter than 2 characters, it returns 0 to avoid noise from very short search terms.
 *
 * @param searchStr
 * @param value
 * @returns
 */
export const getCompareScore = (searchStr: string, value: string): number => {
  const trimmedSearchStr = searchStr.trim();
  if (trimmedSearchStr.length < 2) {
    return 0; // Ignore search strings shorter than 2 characters
  }
  const similarityScore = stringSimilarity.compareTwoStrings(trimmedSearchStr.toLowerCase(), value.toLowerCase());

  // always boost the score if the search string is a substring of the value
  // we boost each matching character by 0.1, so a 10-character match will add 1 to the score
  // this is to ensure that partial matches are still considered relevant
  const substringBoost = value.toLowerCase().includes(trimmedSearchStr.toLowerCase()) ? trimmedSearchStr.length / 10 : 0;

  return Math.min(similarityScore + substringBoost, 1);
};

/**
 * Returns matching search results from a collection, arranged in descending order by relevance score
 * @param collection
 * @param searchStr
 * @param key
 */
export const searchCollectionByKey = <T>(collection: Array<T>, searchStr: string, key: keyof T): Array<T & { score: number }> =>
  collection
    .map((item) => {
      const value = item[key];
      return typeof value === "string"
        ? {
            ...item,
            score: getCompareScore(searchStr, value),
          }
        : { ...item, score: 0 };
    })
    .filter((item) => item.score > MINIMUM_SEARCH_SCORE)
    .sort((a, b) => b.score - a.score);
